import { useState } from "react";
import { Form, Input, Select, Row, Col, Spin, Checkbox } from "antd";
import Layout from "../../../Containers/Layout";
import { Link, Redirect, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getShortSubject, updateShortSubject, addShortSubject } from "../../../Requests/subject-requests";
import { NOTIFICATION_TYPES, openNotification } from "../../Notifications/NotificationsUtils";
import { getUserId } from "../../../utils/utilFunctions";
import { theme } from "../../../theme";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import { AcademicUser, AddSubjectDTO, EvaluationTypeEnum, IndicatorTypeEnum, SemesterTypeEnum, StudyCycleEnum, StudyPlanDTO, StudyYearEnum } from "../../../Api";
import { DisciplineCategoryEnum } from "../../../Api";
import styles from "./SubjectShortForm.module.scss";
import { getStudyProgram } from "../../../Requests/study-program-request";
import useDebounce from "../../../Hooks/debounce";
import { getDepartmentsForFaculty } from "../../../Requests/faculty-requests";
import { getAllAcademicUsers } from "../../../Requests/academic-user-requests";

const { Option } = Select;

const SubjectShortForm = (props: any) => {
  const { t } = useTranslation();
  const currentUserId = getUserId();
  const { subjectId } = useParams<{ subjectId: string }>();
  const [redirect, setRedirect] = useState(false);
  const { programId, studyCycle } = useParams<{ programId: string, studyCycle: StudyCycleEnum }>();
  const isDoctorate = studyCycle === StudyCycleEnum.Doctorate;

  const [searchTermNameCourse, setCourseUserSearchTerm] = useState('');
	const debouncedCourseUserSearch = useDebounce(searchTermNameCourse, 1000);

  const [searchTermNameLab, setLabUserSearchTerm] = useState('');
	const debouncedLabUserSearch = useDebounce(searchTermNameLab, 1000);

  const location = useLocation();

  const plan = location.state as StudyPlanDTO;

  
  
  const [form] = Form.useForm<{
    orderNumber: number;
    nameRO: string | null;
    nameEN: string | null;
    studyProgramId: string; 
    departmentId: string; 
    evaluationType: EvaluationTypeEnum;
    formativeCategory: IndicatorTypeEnum;
    subjectCategory: IndicatorTypeEnum;
    code: string | null;
    courseWeeklyHours: number;
    labWeeklyHours: number;
    seminarWeeklyHours: number;
    projectWeeklyHours: number;
    selfStudyHours: number;
    tutoringHours: number;
    credits?: number;
    creditsIIS?: number;
    creditsOE?: number;
    planYear?: number;
    studyYear?: StudyYearEnum;
    semester?: SemesterTypeEnum;
    courseCoordinatorIds: string[] | null;
    labCoordinatorIds: string[] | null;
    disciplineCategory: DisciplineCategoryEnum;
    practiceTotalHours: number;
    labAtPartner?: boolean;
    seminarAtPartner?: boolean;
    projectAtPartner?: boolean;
    practiceAtPartner?: boolean;

  }>();

  const openGetErrorNotification = (_error: any) => {
    openNotification(
      'Eroare',
      'Nu s-au putut prelua datele de pe server!',
      NOTIFICATION_TYPES.ERROR
    );
  };
  
  const { data: program } = useQuery(
    ["getProgram", programId], 
    () => getStudyProgram(programId)
  );
  
  const { data: departments } = useQuery(
    ['getDepartments'],
    () => {
      const faculties = program?.facultyId ? [program.facultyId] : [];
      return getDepartmentsForFaculty(faculties);
    },
    {
      enabled: !!program?.facultyId,
      onError: openGetErrorNotification,
    }
  );

  const { data: courseUsers, isLoading: isLoadingCourseSearch } = useQuery(['getAcademiUsersCourse', debouncedCourseUserSearch],
    () => {
        const response = getAllAcademicUsers(debouncedCourseUserSearch, 1, 10, undefined);
        return response;
    },
    {
        onError: openGetErrorNotification,
    }
  );

  const { data: labUsers, isLoading: isLoadingLabSearch } = useQuery(['getAcademiUsersLab', debouncedLabUserSearch],
    () => getAllAcademicUsers(debouncedLabUserSearch, 1, 10, undefined),
    {
        onError: openGetErrorNotification,
    }
  );

  const { data: subject } = useQuery(["getSubject", subjectId], () => getShortSubject(subjectId), {
    onError: (err) => {
      openGetErrorNotification(err);
    },
    onSuccess: (data) => {
      console.log("plan", plan);
      
      if (data) {
        const hasSplitCredits = (Number(data.creditsIIS ?? 0) + Number(data.creditsOE ?? 0)) > 0;
        const normalizedCreditsIIS = hasSplitCredits ? Number(data.creditsIIS ?? 0) : Number(data.credits ?? 0);
        const normalizedCreditsOE = hasSplitCredits ? Number(data.creditsOE ?? 0) : 0;

        form.setFieldsValue({
          orderNumber: data.orderNumber,
          nameRO: data.nameRO,
          nameEN: data.nameEN,
          studyProgramId: data.studyProgramId,
          departmentId: plan.departmentId,
          evaluationType: data.evaluationType,
          formativeCategory: data.formativeCategory,
          subjectCategory: data.subjectCategory,
          code: data.code,
          courseWeeklyHours: data.courseWeeklyHours,
          labWeeklyHours: data.labWeeklyHours,
          seminarWeeklyHours: data.seminarWeeklyHours,
          projectWeeklyHours: data.projectWeeklyHours,
          credits: normalizedCreditsIIS + normalizedCreditsOE,
          creditsIIS: normalizedCreditsIIS,
          creditsOE: normalizedCreditsOE,
          planYear: data.planYear,
          studyYear: data.studyYear,
          semester: data.semester,
          courseCoordinatorIds: data.courseCoordinators?.map((user: AcademicUser) => user.id || '') || [],
          labCoordinatorIds: data.labCoordinators?.map((user: AcademicUser) => user.id || '') || [],
          disciplineCategory: data.disciplineCategory ?? DisciplineCategoryEnum.Standard,
          practiceTotalHours: data.practiceTotalHours ?? 0,
          labAtPartner: data.labAtPartner ?? false,
          seminarAtPartner: data.seminarAtPartner ?? false,
          projectAtPartner: data.projectAtPartner ?? false,
          practiceAtPartner: data.practiceAtPartner ?? false,
        });
      }
    },
    enabled: !!subjectId,
    refetchOnWindowFocus: false
  });

  const openSaveErrorNotification = (error: any) => {
    openNotification(
      'Eroare!',
      'Nu s-a putut salva disciplina!',
      NOTIFICATION_TYPES.ERROR
    );
  };

  const handleCancel = () => {
    setRedirect(true);
  };

  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 4 }, (_, i) => currentYear - i);

  const category = Form.useWatch('disciplineCategory', form);

  const openSuccessNotificationAndRedirect = () => {
    setRedirect(true);
    openNotification(
      'Succes!',
      'Disciplina a fost salvată cu succes!',
      NOTIFICATION_TYPES.SUCCESS,
      6
    );
  };

  const handleSave = () => {
    const values = form.getFieldsValue();
    const isDualProgram = !!program?.isDualProgram;
    const creditsIIS = isDualProgram ? (Number(values.creditsIIS) || 0) : (Number(values.credits) || 0);
    const creditsOE = isDualProgram ? (Number(values.creditsOE) || 0) : 0;
    
    // Fără an/semestru, backend-ul salvează default L1/S1 -> disciplina dispare din UI dar apare în PDF
    if (values.nameRO && (isDoctorate || values.studyYear) && values.semester) {
      const subjectData: AddSubjectDTO = {
        orderNumber: values.orderNumber,
        nameRO: values.nameRO,
        nameEN: values.nameEN,
        studyProgramId: program?.id,
        departmentId: plan.departmentId,
        evaluationType: isDoctorate ? EvaluationTypeEnum.V : values.evaluationType,
        formativeCategory: values.formativeCategory,
        subjectCategory: values.subjectCategory,
        code: values.code,
        courseWeeklyHours: values.courseWeeklyHours,
        labWeeklyHours: values.labWeeklyHours,
        seminarWeeklyHours: values.seminarWeeklyHours,
        projectWeeklyHours: values.projectWeeklyHours,
        credits: creditsIIS + creditsOE,
        creditsIIS,
        creditsOE,
        planYear: subject?.planYear, 
        studyYear: isDoctorate ? StudyYearEnum.D1 : values.studyYear,
        semester: values.semester,
        courseCoordinatorIds: values.courseCoordinatorIds,
        labCoordinatorIds: values.labCoordinatorIds,
        disciplineCategory: isDoctorate ? DisciplineCategoryEnum.PracticeStage : values.disciplineCategory,
        practiceTotalHours: values.practiceTotalHours ?? 0,
        labAtPartner: !!values.labAtPartner,
        seminarAtPartner: !!values.seminarAtPartner,
        projectAtPartner: !!values.projectAtPartner,
        practiceAtPartner: !!values.practiceAtPartner,
        studyPlanId: plan.id,
      };
      
      if (subjectData.disciplineCategory === DisciplineCategoryEnum.PracticeStage) {
        subjectData.courseWeeklyHours = 0;
        subjectData.labWeeklyHours = 0;
        subjectData.seminarWeeklyHours = 0;
        subjectData.projectWeeklyHours = 0;
      }

      const saveAction = subjectId ? updateShortSubject(subjectId, subjectData) : addShortSubject(subjectData);

      saveAction.then(openSuccessNotificationAndRedirect).catch(openSaveErrorNotification);
    } else {
      openNotification(
        'Eroare!',
        'Vă rugăm să completați toate câmpurile obligatorii!',
        NOTIFICATION_TYPES.ERROR
      );
    }
  };

  if (redirect) {
    return <Redirect to={{ pathname: '/' + studyCycle + '/plan/' + program?.id}}></Redirect>;
  }

  return (
    <Layout>
      <div className="container">
        <Form form={form} layout="vertical">
          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex' }}>1. Date despre program</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Instituția de învățământ superior</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Universitatea Națională de Știință și Tehnologie POLITEHNICA București</span>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Facultatea</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.faculty?.name}</span>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Departamentul</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px", textAlign: "left" }}>
                {plan?.department ? plan.department.departmentNameRo : "Facultate"}
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Domeniul de studii</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.domainName}</span>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Specializarea</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{program?.name}</span>
              </Col>
            </Row>
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Ciclul de studii</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>{ studyCycle === StudyCycleEnum.Bachelor ? "Licență" : "Master"}</span>
              </Col>
            </Row>
            
            
          </div>


          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>2. Date despre disciplină</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            <Row gutter={16}   style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Număr de ordine disciplina</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="orderNumber"
              >
                <Input placeholder="Număr de ordine" />
                
              </Form.Item>

              </Col>
            </Row>

            <Row gutter={16}   style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Denumirea disciplinei (ro)(en)</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="nameRO"
              >
                <Input placeholder="Numele disciplinei RO" />
                
              </Form.Item>

              <Form.Item
                name="nameEN"
              >
                <Input placeholder="Numele disciplinei EN" />
              </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Titularul activităților de curs</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="courseCoordinatorIds"
              >
                <Select
                            className={styles.antSelect}
                            mode="multiple"
                            onSelect={() => setCourseUserSearchTerm('')}
                            maxTagCount={10}
                            allowClear
                            showSearch
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder={"Căutare titular curs"}
                            filterOption={false}
                            onSearch={setCourseUserSearchTerm}
                            notFoundContent={
                              isLoadingCourseSearch? <Spin size="small" /> : null
                            }
                        >
                            {[...(subject?.courseCoordinators || []),
                              ...courseUsers?.data?.filter(user => !(subject?.courseCoordinators || []).find(u => u.id === user.id)) || []]
                                ?.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </Option>
                                ))
                                }
                        </Select>
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={10} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Titularul activităților de laborator</span>
              </Col>
              <Col span={14} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="labCoordinatorIds"
              >
                <Select
                            className={styles.antSelect}
                            mode="multiple"
                            onSelect={() => setLabUserSearchTerm('')}
                            maxTagCount={10}
                            allowClear
                            showSearch
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder={"Căutare titular laborator"}
                            filterOption={false}
                            onSearch={setLabUserSearchTerm}
                            notFoundContent={
                              isLoadingLabSearch ? <Spin size="small" /> : null
                            }
                        >
                            {[...(subject?.labCoordinators || []),
                              ...labUsers?.data?.filter(user => !(subject?.labCoordinators || []).find(u => u.id === user.id)) || []]
                                ?.map((user) => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name} - {user.email}
                                    </Option>
                                ))
                                }
                        </Select>
              </Form.Item>
              </Col>
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
              <Col span={4} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Anul de studiu</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="studyYear"
                rules={[{ required: !isDoctorate, message: "Selectați anul de studii!" }]}
              >
                <Select disabled={isDoctorate} style={{ width: "100%" }} placeholder="Selectați anul de studii">
                  {studyCycle === "Bachelor" && (
                    <>
                      <Option value="L1">L1</Option>
                      <Option value="L2">L2</Option>
                      <Option value="L3">L3</Option>
                      <Option value="L4">L4</Option>
                    </>
                  )}
                  
                  {studyCycle === "Master" && (
                    <>
                      <Option value="M1">M1</Option>
                      <Option value="M2">M2</Option>
                    </>
                  )} 
                  {studyCycle === "Doctorate" && (
                    <>
                      <Option value="D1">D1</Option>
                    </>
                  )}
                </Select>
              </Form.Item>
              </Col>

              <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Semestrul</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="semester"
                rules={[{ required: true, message: "Selectați semestrul!" }]}
              >
                <Select style={{width: "100%"}} placeholder="Selectați semestrul">
                  <Option value="S1">Semestrul 1</Option>
                  <Option value="S2">Semestrul 2</Option>
                </Select>
              </Form.Item>
              </Col>

              <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Forma de evaluare</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="evaluationType"
              >
                <Select disabled={isDoctorate} style={{width: "100%"}} placeholder="Selectați forma de evaluare">
                  <Option value={EvaluationTypeEnum.E}>Examen</Option>
                  <Option value={EvaluationTypeEnum.V}>Verificare</Option>
                  <Option value={EvaluationTypeEnum.C}>Colocviu</Option>
                </Select>
              </Form.Item>
              </Col>
              
              
            </Row>

            <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>

              <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Regimul disciplinei</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="subjectCategory"
              >
                <Select style={{ width: "100%" }} placeholder="Selectați regimul disciplinei">
                  <Option value="Mandatory">Obligatorie</Option>
                  <Option value="Optional">Opțională</Option>
                  <Option value="Facultative">Facultativă</Option>
                </Select>
              </Form.Item>
              </Col>

              <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey", padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Categoria formativă</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
              <Form.Item
                name="formativeCategory"
              >
                <Select style={{ width: "100%" }} placeholder="Selectați categoria formativă">
                  <Option value="Fundamental">Fundamental</Option>
                  <Option value="Specialty">Specializare</Option>
                  <Option value="Complementary">Complementară</Option>
                </Select>
              </Form.Item>
              </Col>

              
              <Col span={4} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                <span style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Categorie disciplină</span>
              </Col>
              <Col span={4} style={{ padding: "8px 16px" }}>
                <Form.Item
                  name="disciplineCategory"
                  initialValue={isDoctorate ? DisciplineCategoryEnum.PracticeStage : DisciplineCategoryEnum.Standard}
                  rules={[{ required: true, message: "Selectați categoria disciplinei!" }]}
                >
                  <Select disabled={isDoctorate} style={{ width: "100%" }} placeholder="Selectați categoria disciplinei">
                    <Option value="Standard">Standard</Option>
                    <Option value="PracticeStage">Practică</Option>
                    <Option value="DiplomaProject">Elaborare proiect diplomă</Option>
                  </Select>
                </Form.Item>
              </Col>

              
            </Row>
          </div>

          <Row gutter={16}><Col span={24}><span style={{ fontSize: '16px', fontWeight: 'bold', display: 'flex', marginTop: "15px" }}>3. Timpul total estimat (ore pe semestru al activităților didactice)</span></Col></Row>
          <div style={{
            border: "1px solid lightgrey", 
            borderRadius: "5px", 
            overflow: "hidden",
          }}>
            {category === DisciplineCategoryEnum.PracticeStage ? (
              <>
              <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                <Col span={21} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>
                    Număr total de ore (practică)
                  </span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item
                    name="practiceTotalHours"
                    initialValue={0}
                    rules={[{ required: true, message: "Introduceți numărul total de ore" }]}
                  >
                    <Input type="number" min={0} />
                  </Form.Item>
                </Col>
              </Row>
              {program?.isDualProgram && (
                <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                  <Col span={21} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                    <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>
                      Practica desfășurată la operatorul economic
                    </span>
                  </Col>
                  <Col span={3} style={{ padding: "8px 16px" }}>
                    <Form.Item name="practiceAtPartner" valuePropName="checked" initialValue={false}>
                      <Checkbox>Da</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              )}
              </>
            ) : (
              <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Număr de ore curs</span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item name="courseWeeklyHours"><Input/></Form.Item>
                </Col>

                <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey", padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Număr de ore laborator</span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item name="labWeeklyHours"><Input/></Form.Item>
                  {program?.isDualProgram && (
                    <Form.Item name="labAtPartner" valuePropName="checked" initialValue={false}>
                      <Checkbox>Laborator la OE</Checkbox>
                    </Form.Item>
                  )}
                </Col>

                <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey",  padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Număr de ore seminar</span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item name="seminarWeeklyHours"><Input/></Form.Item>
                  {program?.isDualProgram && (
                    <Form.Item name="seminarAtPartner" valuePropName="checked" initialValue={false}>
                      <Checkbox>Seminar la OE</Checkbox>
                    </Form.Item>
                  )}
                </Col>

                <Col span={3} style={{ borderRight: "1px solid lightgrey", borderLeft: "1px solid lightgrey", padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Număr de ore proiect</span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item name="projectWeeklyHours"><Input/></Form.Item>
                  {program?.isDualProgram && (
                    <Form.Item name="projectAtPartner" valuePropName="checked" initialValue={false}>
                      <Checkbox>Proiect la OE</Checkbox>
                    </Form.Item>
                  )}
                </Col>
              </Row>
            )}

            
            {program?.isDualProgram ? (
              <>
                <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                  <Col span={21} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                    <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de ECTS (IIS)</span>
                  </Col>
                  <Col span={3} style={{ padding: "8px 16px" }}>
                    <Form.Item name="creditsIIS">
                      <Input type="number" min={0} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                  <Col span={21} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                    <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de ECTS (OE)</span>
                  </Col>
                  <Col span={3} style={{ padding: "8px 16px" }}>
                    <Form.Item name="creditsOE">
                      <Input type="number" min={0} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : (
              <Row gutter={16} style={{ borderBottom: "1px solid lightgrey" }}>
                <Col span={21} style={{ borderRight: "1px solid lightgrey", padding: "8px 16px" }}>
                  <span style={{ display: 'flex', alignItems: 'left', height: '100%' }}>Numărul de ECTS</span>
                </Col>
                <Col span={3} style={{ padding: "8px 16px" }}>
                  <Form.Item name="credits">
                    <Input type="number" min={0} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </div>
          
           <div className={styles.secondLine} style={{ marginTop: "10px" }}>
              <div className={styles.firstColumn} style={{ display: "flex", width: "250px", justifyContent: "space-between " }}>
                <ConfirmationModal
                  nameButton={t('internships.addInternshipForm.cancel')}
                  modalText={t('internships.cancelMessage')}
                  titleButton={''}
                  handleFunction={handleCancel}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
                <ConfirmationModal
                  nameButton={subjectId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                  modalText={t('internships.saveMessage')}
                  titleButton={''}
                  handleFunction={handleSave}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
              </div>
            </div>                    
        </Form>
      </div>
    </Layout>
  );
};

export default SubjectShortForm;